import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { Card } from 'primereact/card';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Message } from 'primereact/message';
import { getBranches, clearDataByDateRange } from '../utilities/api';

const ClearData = () => {
    const [fromDate, setFromDate] = useState(null);
    const [toDate, setToDate] = useState(null);
    const [selectedBranch, setSelectedBranch] = useState('All');
    const [branchOptions, setBranchOptions] = useState([{ label: 'All Branches', value: 'All' }]);

    const [loadingBranches, setLoadingBranches] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [alertInfo, setAlertInfo] = useState(null);

    useEffect(() => {
        let mounted = true;
        const loadBranches = async () => {
            setLoadingBranches(true);
            try {
                const clientId = localStorage.getItem('clientid');
                const branches = await getBranches(clientId);
                if (!mounted) return;
                const mapped = (Array.isArray(branches) ? branches : []).map((b) => ({
                    label: b.branch_name || b.branchname || b.name || String(b.branch_id),
                    value: String(b.branch_id)
                }));
                setBranchOptions([{ label: 'All Branches', value: 'All' }, ...mapped]);
            } catch (err) {
                if (!mounted) return;
                setAlertInfo({
                    severity: 'error',
                    title: 'Branch load failed',
                    detail: err?.response?.data?.message || err?.message || 'Unable to fetch branches.'
                });
            } finally {
                if (mounted) setLoadingBranches(false);
            }
        };
        loadBranches();
        return () => {
            mounted = false;
        };
    }, []);

    const isInvalidRange = useMemo(() => {
        if (!fromDate || !toDate) return false;
        return moment(toDate).isBefore(moment(fromDate), 'day');
    }, [fromDate, toDate]);

    const selectedBranchName = useMemo(() => {
        if (selectedBranch === 'All') return 'All Branches';
        const matched = branchOptions.find((option) => String(option.value) === String(selectedBranch));
        return matched?.label || String(selectedBranch || 'All Branches');
    }, [selectedBranch, branchOptions]);

    const canDelete = !!fromDate && !!toDate && !isInvalidRange && !submitting;

    const onDeleteClick = () => {
        if (!canDelete) {
            setAlertInfo({
                severity: 'warn',
                title: 'Invalid input',
                detail: isInvalidRange ? '`To date` should be greater than or equal to `From date`.' : 'Please select From Date and To Date.'
            });
            return;
        }
        setShowConfirm(true);
    };

    const onConfirmDelete = async () => {
        setSubmitting(true);
        try {
            const payload = {
                from_date: moment(fromDate).format('YYYY-MM-DD'),
                to_date: moment(toDate).format('YYYY-MM-DD'),
                branch_id: selectedBranch || 'All',
                cleared_by: localStorage.getItem('userid') || ''
            };
            const res = await clearDataByDateRange(payload);
            const ok = res?.status === true || String(res?.status || '').toLowerCase() === 'success';
            if (!ok) {
                throw new Error(res?.message || res?.error || 'Clear data request failed.');
            }
            setAlertInfo({
                severity: 'success',
                title: 'Data cleared',
                detail: res?.message || 'Requested data has been cleared successfully.'
            });
            setShowConfirm(false);
        } catch (err) {
            setAlertInfo({
                severity: 'error',
                title: 'Delete failed',
                detail: err?.response?.data?.message || err?.message || 'Failed to clear data.'
            });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="p-3">
            <Card title="Clear Data" subTitle="Delete data by date range and branch">
                {alertInfo ? (
                    <div className="mb-3">
                        <Message severity={alertInfo.severity} text={`${alertInfo.title}: ${alertInfo.detail}`} />
                    </div>
                ) : null}

                <div className="p-fluid formgrid grid">
                    <div className="field col-12 md:col-4">
                        <label htmlFor="clear-from-date">From Date</label>
                        <Calendar id="clear-from-date" value={fromDate} onChange={(e) => setFromDate(e.value)} dateFormat="dd-mm-yy" showIcon showButtonBar placeholder="Select from date" />
                    </div>
                    <div className="field col-12 md:col-4">
                        <label htmlFor="clear-to-date">To Date</label>
                        <Calendar id="clear-to-date" value={toDate} onChange={(e) => setToDate(e.value)} dateFormat="dd-mm-yy" showIcon showButtonBar minDate={fromDate || undefined} placeholder="Select to date" />
                    </div>
                    <div className="field col-12 md:col-4">
                        <label htmlFor="clear-branch">Branch</label>
                        <Dropdown id="clear-branch" value={selectedBranch} options={branchOptions} onChange={(e) => setSelectedBranch(e.value)} placeholder="Select branch" filter disabled={loadingBranches} />
                    </div>
                </div>

                {isInvalidRange ? <small className="p-error block mb-3">`To date` should not be less than `From date`.</small> : null}

                <div className="mt-3">
                    <Button label={submitting ? 'Deleting...' : 'Delete Data'} icon="pi pi-trash" className="p-button-danger" onClick={onDeleteClick} disabled={!canDelete} loading={submitting} />
                </div>
            </Card>

            <Dialog
                header="Confirm Delete"
                visible={showConfirm}
                modal
                closable={!submitting}
                style={{ width: 'min(520px, 96vw)' }}
                onHide={() => !submitting && setShowConfirm(false)}
                footer={
                    <div className="flex gap-2 justify-content-end">
                        <Button label="Cancel" className="p-button-text" onClick={() => setShowConfirm(false)} disabled={submitting} />
                        <Button label="Yes, Delete" icon="pi pi-trash" className="p-button-danger" onClick={onConfirmDelete} loading={submitting} />
                    </div>
                }
            >
                <p className="m-0">
                    Are you sure you want to delete data from <strong>{fromDate ? moment(fromDate).format('DD-MM-YYYY') : '-'}</strong> to <strong>{toDate ? moment(toDate).format('DD-MM-YYYY') : '-'}</strong> for branch{' '}
                    <strong>{selectedBranchName}</strong>?
                </p>
            </Dialog>
        </div>
    );
};

export default ClearData;
